import AdjusterInformation from "@/components/Company/AdjusterInformation.tsx";
import UiUmAdjusterAutocomplete from "@/components/Forms/UiUmAdjusterAutocomplete.tsx";
import UiUmInsuranceCompanyAutocomplete from "@/components/Forms/UiUmInsuranceCompanyAutocomplete.tsx";
import { addUiUmSchema } from "@/components/Forms/schema/addUiUmSchema.ts";
import { useAddEditUmUimToCase } from "@/mutations/useAddEditUmUimToCase.tsx";
import { type AddPersonResponse, useAddPerson } from "@/mutations/useAddPerson.tsx";
import { useFetchParty } from "@/queries/useFetchParty.ts";
import type { Party } from "@/types/Party.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    Link,
    MenuItem,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

const massageDataForForm = (data: Partial<Party>): Partial<AddUiUmFormValues> => {
    return {
        ...data,
        claimNumber: data.claimNumber ?? "",
        idAdjuster: data.idAdjuster ?? "",
        namePrefix: data.namePrefix ?? "",
        nameFirst: data.nameFirst ?? "",
        nameI: data.nameI ?? "",
        nameLast: data.nameLast ?? "",
        nameSuffix: data.nameSuffix ?? "",
        nameAKA: data.nameAKA ?? "",
        phoneExtension: data.phoneExtension ?? "",
        email: data.email ?? "",
        address: data.address ?? "",
        address2: data.address2 ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        policyPersonCoverageMM: data.policyPersonCoverageMM ?? "",
        policyVehiculeCoverageMM: data.policyVehiculeCoverageMM ?? "",
        policyInsuredName: data.policyInsuredName ?? "",
        policyDriverName: data.policyDriverName ?? "",
        coverageType: data.coverageType ?? "",
        formLimits: data.formLimits ?? "",
        medpay: data.medpay ?? "",
    };
};

export type AddUiUmFormValues = z.infer<typeof addUiUmSchema>;

type Props = {
    caseUUID: string;
    showAddButton?: boolean;
    editPartyId?: string;
    clearEditPartyId?: () => void;
};

const AddEditUmUimDialog = ({
    caseUUID,
    editPartyId,
    clearEditPartyId,
    showAddButton = true,
}: Props): ReactNode => {
    const addUiUmToCaseMutation = useAddEditUmUimToCase(caseUUID);
    const addPersonMutation = useAddPerson();
    const [open, setOpen] = useState(false);
    const [isAddingAdjuster, setIsAddingAdjuster] = useState(false);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchParty({
        partyId: editPartyId ?? null,
        isQueryEnabled: editPartyId !== undefined,
    });

    const form = useForm<AddUiUmFormValues>({
        resolver: zodResolver(addUiUmSchema),
        defaultValues: {},
    });

    const handleSubmit = useCallback(
        (values: AddUiUmFormValues) => {
            addUiUmToCaseMutation.mutate(
                {
                    ...values,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("UM/UIM added.", { variant: "success" });
                        if (clearEditPartyId) {
                            clearEditPartyId();
                        }

                        form.reset({});
                        setOpen(false);
                        setIsAddingAdjuster(false);
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Add UM/UIM failed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        },
        [addUiUmToCaseMutation, form.reset, clearEditPartyId],
    );

    const onSubmit: SubmitHandler<AddUiUmFormValues> = async (
        data: AddUiUmFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    const handleShowAddAdjusterForm = useCallback(() => {
        form.setValue("idAdjuster", "");

        const adjusterFields = [
            "namePrefix",
            "nameFirst",
            "nameI",
            "nameLast",
            "nameSuffix",
            "nameAKA",
            "primaryPhoneNumber",
            "phoneExtension",
            "faxNumber",
            "email",
            "address",
            "address2",
            "city",
            "state",
            "zip",
        ];

        adjusterFields.forEach((field) => {
            form.setValue(field as keyof AddUiUmFormValues, "");
        });

        setIsAddingAdjuster(true);
    }, [form]);

    const handleSaveAdjuster = useCallback(() => {
        const values = form.getValues();

        return addPersonMutation.mutate(
            {
                role: "UM/UIM Adjuster",
                namePrefix: values.namePrefix ?? "",
                nameFirst: values.nameFirst ?? "",
                nameI: values.nameI ?? "",
                nameLast: values.nameLast ?? "",
                nameSuffix: values.nameSuffix ?? "",
                nameAKA: values.nameAKA ?? "",
                primaryPhoneNumber: values.phoneNumber ?? "",
                phoneExtension: values.phoneExtension ?? "",
                email: values.email ?? "",
                address: values.address ?? "",
                address2: values.address2 ?? "",
                city: values.city ?? "",
                state: values.state ?? "",
                zip: values.zip ?? "",
            },
            {
                onSuccess: (newPerson: AddPersonResponse) => {
                    form.setValue("idAdjuster", newPerson.personUUID);
                    enqueueSnackbar("Adjuster added successfully.", { variant: "success" });
                    setIsAddingAdjuster(false);
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Add Adjuster failed.", { variant: "error" });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    }, [form, addPersonMutation]);

    const handleCancelAddAdjuster = useCallback(() => {
        const adjusterFields = [
            "namePrefix",
            "nameFirst",
            "nameI",
            "nameLast",
            "nameSuffix",
            "nameAKA",
            "primaryPhoneNumber",
            "phoneExtension",
            "faxNumber",
            "email",
            "address",
            "address2",
            "city",
            "state",
            "zip",
        ];

        adjusterFields.forEach((field) => {
            form.setValue(field as keyof AddUiUmFormValues, "");
        });

        setIsAddingAdjuster(false);
    }, [form]);

    const handleCloseDialog = useCallback(() => {
        if (clearEditPartyId) {
            clearEditPartyId();
        }
        form.reset({});
        setOpen(false);
        setIsAddingAdjuster(false);
    }, [clearEditPartyId, form]);

    useEffect(() => {
        if (data) {
            form.reset(massageDataForForm(data));
            setOpen(true);
        }
    }, [data, form]);

    return (
        <>
            {showAddButton && (
                <Button
                    variant={"contained"}
                    sx={{
                        ml: 2,
                        minWidth: "120px",
                    }}
                    onClick={() => {
                        form.reset({});
                        setOpen(true);
                    }}
                >
                    {editPartyId ? "Edit" : "New"} UM/UIM
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>
                            {editPartyId ? "Edit" : "New"} UM/UIM - Bodily Injury
                        </DialogTitle>
                        <DialogContent dividers>
                            {(isLoading || isRefetching) && <CircularProgress />}
                            {isError && <Alert severity={"error"}>Error loading form data.</Alert>}
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"claimNumber"}
                                        label={"Claim Number"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <UiUmInsuranceCompanyAutocomplete form={form} />
                                </Grid>
                                <Grid size={12}>
                                    <UiUmAdjusterAutocomplete form={form} />
                                    {!isAddingAdjuster && (
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={handleShowAddAdjusterForm}
                                            sx={{
                                                color: "#4CAF50",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                                fontSize: "14px",
                                                display: "block",
                                                mt: 1,
                                            }}
                                        >
                                            Add Adjuster
                                        </Link>
                                    )}
                                </Grid>
                                <Grid size={12}>
                                    <AdjusterInformation
                                        onSaveAdjuster={handleSaveAdjuster}
                                        onCancelAddAdjuster={handleCancelAddAdjuster}
                                        isAddingAdjuster={isAddingAdjuster}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"policyPersonCoverageMM"}
                                        label={"UM/UIM Coverage"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"policyVehiculeCoverageMM"}
                                        label={"Property Damage Coverage"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"policyInsuredName"}
                                        label={"Insured Name *"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"policyDriverName"}
                                        label={"Driver Name"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"coverageType"}
                                        label={"Coverage Type"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    >
                                        {["", "Add-on", "Reduced By"].map((limit) => (
                                            <MenuItem key={`coverage-limit-${limit}`} value={limit}>
                                                {limit}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"formLimits"}
                                        label={"Limits"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"medpay"}
                                        label={"MedPay"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                {editPartyId ? "Edit" : "New"} UM/UIM
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddEditUmUimDialog;
